import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { verifyMagicLink } from "../api/auth.js";
import { ApiCallError } from "../api/client.js";
import { useQueryClient } from "@tanstack/react-query";

type ViewState = "verifying" | "success" | "error";

export function VerifyPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [view, setView] = useState<ViewState>("verifying");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;

    const token = params.get("token");
    if (!token) {
      setErrorMsg(
        "No token found in the URL. Please use the link from your email.",
      );
      setView("error");
      return;
    }

    // Timeout: if verification takes longer than 15s, show an escape
    const timeout = setTimeout(() => {
      setErrorMsg(
        "Verification is taking longer than expected. Please try signing in again.",
      );
      setView("error");
    }, 15_000);

    verifyMagicLink(token)
      .then(() => {
        clearTimeout(timeout);
        setView("success");
        void qc.invalidateQueries({ queryKey: ["session"] });
        const returnTo = sessionStorage.getItem("auth_return_to");
        sessionStorage.removeItem("auth_return_to");
        setTimeout(() => void navigate(returnTo || "/app"), 1200);
      })
      .catch((err: unknown) => {
        clearTimeout(timeout);
        setErrorMsg(
          err instanceof ApiCallError
            ? err.message
            : "Verification failed. The link may have expired or already been used.",
        );
        setView("error");
      });
  }, [params, navigate, qc]);

  return (
    <div className="auth-layout">
      <div className="auth-card">
        <div className="auth-card__logo">
          Accessi<span>Monitor</span>
        </div>

        {view === "verifying" && (
          <>
            <div className="auth-spinner" aria-hidden="true" />
            <h1 className="auth-card__title">Verifying…</h1>
            <p className="auth-card__subtitle">
              Please wait while we sign you in.
            </p>
          </>
        )}

        {view === "success" && (
          <>
            <div className="auth-success-icon" aria-hidden="true">✓</div>
            <h1 className="auth-card__title">Signed in</h1>
            <p className="auth-card__subtitle">Redirecting you now…</p>
          </>
        )}

        {view === "error" && (
          <>
            <h1 className="auth-card__title">Sign-in failed</h1>
            <div className="alert alert--error" role="alert">
              {errorMsg}
            </div>
            <Link to="/sign-in" className="btn btn--primary">
              Back to sign in
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
